import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  type DocumentData,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CropType {
  id: string;
  name: string;
  isDeleted: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  deletedAt?: Timestamp;
}

export interface CreateCropTypeData {
  name: string;
}

export interface CropTypeServiceError {
  code: string;
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function docToCropType(id: string, data: DocumentData): CropType {
  return {
    id,
    name: data.name as string,
    isDeleted: data.isDeleted as boolean,
    createdAt: data.createdAt as Timestamp,
    updatedAt: data.updatedAt as Timestamp,
    deletedAt: data.deletedAt as Timestamp | undefined,
  };
}

function toServiceError(error: unknown): CropTypeServiceError {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: error.message };
  }
  return { code: 'unknown', message: 'An unexpected error occurred' };
}

/** Returns true if a non-deleted crop type with the given name already exists in the org. */
async function nameExists(orgId: string, name: string, excludeId?: string): Promise<boolean> {
  const q = query(
    collection(db, 'orgs', orgId, 'cropTypes'),
    where('isDeleted', '==', false),
    where('name', '==', name.trim()),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.some(d => d.id !== excludeId);
}

// ─── Service functions ────────────────────────────────────────────────────────

/** Returns all non-deleted crop types in an org, alphabetically. */
export async function getAllCropTypes(orgId: string): Promise<CropType[]> {
  try {
    const q = query(
      collection(db, 'orgs', orgId, 'cropTypes'),
      where('isDeleted', '==', false),
      orderBy('name', 'asc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => docToCropType(d.id, d.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Returns a single crop type by document ID, or null if not found. */
export async function getCropTypeById(orgId: string, id: string): Promise<CropType | null> {
  try {
    const snapshot = await getDoc(doc(db, 'orgs', orgId, 'cropTypes', id));
    if (!snapshot.exists()) return null;
    return docToCropType(snapshot.id, snapshot.data());
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Creates a new crop type in an org. Throws if the name already exists.
 * Uses the trimmed name as the document ID (slugified) to aid readability;
 * Firestore auto-ID would also work — the compound ID pattern is reserved for
 * seasonTimes and customerCrops where uniqueness is enforced at the storage layer.
 */
export async function createCropType(orgId: string, data: CreateCropTypeData): Promise<CropType> {
  try {
    const trimmedName = data.name.trim();
    if (await nameExists(orgId, trimmedName)) {
      throw { code: 'already-exists', message: `A crop type named "${trimmedName}" already exists.` };
    }
    const ref = doc(collection(db, 'orgs', orgId, 'cropTypes'));
    await setDoc(ref, {
      name: trimmedName,
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const snapshot = await getDoc(ref);
    return docToCropType(snapshot.id, snapshot.data()!);
  } catch (error) {
    if ((error as CropTypeServiceError).code) throw error;
    throw toServiceError(error);
  }
}

/** Updates the name of an existing crop type. Throws if the new name already exists. */
export async function updateCropType(orgId: string, id: string, name: string): Promise<void> {
  try {
    const trimmedName = name.trim();
    if (await nameExists(orgId, trimmedName, id)) {
      throw { code: 'already-exists', message: `A crop type named "${trimmedName}" already exists.` };
    }
    await updateDoc(doc(db, 'orgs', orgId, 'cropTypes', id), {
      name: trimmedName,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    if ((error as CropTypeServiceError).code) throw error;
    throw toServiceError(error);
  }
}

/** Soft-deletes a crop type (sets isDeleted + deletedAt). */
export async function deleteCropType(orgId: string, id: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'orgs', orgId, 'cropTypes', id), {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Subscribes to real-time updates for all active crop types in an org.
 * Returns an unsubscribe function to clean up the listener.
 */
export function subscribeToCropTypes(
  orgId: string,
  onData: (_cropTypes: CropType[]) => void,
  onError: (_error: CropTypeServiceError) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'orgs', orgId, 'cropTypes'),
    where('isDeleted', '==', false),
    orderBy('name', 'asc'),
  );
  return onSnapshot(
    q,
    snapshot => onData(snapshot.docs.map(d => docToCropType(d.id, d.data()))),
    err => onError(toServiceError(err)),
  );
}
